<template>
  <div class="event-info-wrapper">
    <div class="container">
      <!-- Header Section -->
      <div class="content-header mb-6">
        <div class="header-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <div>
          <h1>Táncóra részletei</h1>
          <p>Tekintse meg az esemény adatait és résztvevőit</p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <p class="mt-4 loading-text">Betöltés...</p>
        </div>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <!-- Event Details Card -->
      <div v-if="!loading && filteredGroups && filteredGroups.length > 0" class="event-details-card mb-6">
        <div class="card-header">
          <h3>Esemény információk</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Csoport neve
              </div>
              <div class="info-value">{{ getGroupNameById(filteredGroups[0].grp_id) }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Dátum
              </div>
              <div class="info-value">{{ formatDate(filteredGroups[0].set_date) }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                Státusz
              </div>
              <div class="info-value">
                <span class="status-badge" :class="getStatusClass(filteredGroups[0].status.String)">
                  {{ statusEnum[filteredGroups[0].status.String] }}
                </span>
              </div>
            </div>
            
            <div class="info-item" v-if="filteredGroups[0].homework.String">
              <div class="info-label">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                Házifeladat
              </div>
              <div class="info-value">{{ filteredGroups[0].homework.String }}</div>
            </div>
            
            <div class="info-item" v-if="filteredGroups[0].notes.String">
              <div class="info-label">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                </svg>
                Megjegyzések
              </div>
              <div class="info-value">{{ filteredGroups[0].notes.String }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Participants Table -->
      <div v-if="!loading && filteredGroups && filteredGroups.length > 0" class="modern-card">
        <div class="section-header">
          <div class="section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h2>Résztvevők ({{ filteredGroups.length }} táncos)</h2>
          <div class="section-actions" v-if="(isAdmin || isTeacher)">
            <button class="modern-btn modern-btn--success" @click="allPresent">Összes itt van</button>
            <button class="modern-btn modern-btn--warning" @click="completeLesson" v-if="!isCompleted">Lezárás / számlázás</button>
            <button class="modern-btn modern-btn--danger" @click="showCancel = true" v-if="!isCompleted">Tanár lemondja az órát</button>
          </div>
        </div>
        
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Táncos neve</th>
                <th>Válasz</th>
                <th>Jelenlét</th>
                <th v-if="remTime.hours > 1" class="actions-column">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in filteredGroups" :key="event.evnt_id" :class="{ 'debt-row': balances[event.child_id] }">
                <td>
                  <div class="participant-info">
                    <div class="participant-avatar">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <span class="participant-name">{{ event.child_name }}</span>
                  </div>
                </td>
                <td>
                  <span class="response-badge" :class="getResponseClass(event.response.Int64)">
                    {{ event.resp }}
                  </span>
                </td>
                <td>
                  <span class="attendance-badge present">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Jelen
                  </span>
                </td>
                <td v-if="remTime.hours <= 1" class="actions-column">
                  <div class="action-buttons attendance-states">
                    <button
                      class="modern-btn modern-btn--success btn-sm"
                      :class="{ active: (attendanceState[event.child_id] || defaultState(event)) == 1 }"
                      @click="setAttendance(event.child_id, 1)"
                      title="Jelen (Pipa)"
                    >Pipa</button>
                    <button
                      class="modern-btn modern-btn--danger btn-sm"
                      :class="{ active: (attendanceState[event.child_id] || defaultState(event)) == 2 }"
                      @click="setAttendance(event.child_id, 2)"
                      title="Nincs jelen"
                    >Nincs jelen</button>
                    <button
                      class="modern-btn modern-btn--warning btn-sm"
                      :class="{ active: (attendanceState[event.child_id] || defaultState(event)) == 3 }"
                      @click="setAttendance(event.child_id, 3)"
                      title="Opciós (egyedi levonás)"
                    >Opciós</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!filteredGroups || filteredGroups.length === 0)" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" stroke-width="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <h3>Nincs adat</h3>
        <p>Az esemény adatai nem találhatók.</p>
      </div>

      <!-- Teacher cancellation mdl -->
      <div v-if="showCancel" class="mdl-overlay" @click="showCancel = false">
        <div class="mdl-content" @click.stop>
          <div class="mdl-header">
            <h3>Tanár lemondja az órát</h3>
            <button class="close-btn" @click="showCancel = false">✕</button>
          </div>
          <div class="mdl-body">
            <label>Indok (kötelező):</label>
            <textarea v-model="cancelReason" placeholder="Miért mondja le az órát?" class="modern-input"></textarea>
            <div class="mdl-actions">
              <button class="modern-btn modern-btn--danger" :disabled="!cancelReason" @click="cancelClass">Lemondás rögzítése</button>
              <button class="modern-btn" @click="showCancel = false">Mégse</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import axios from 'axios';
  
  export default {
    data() {
      return {
        groups: null,
        resp_enum: {
          0: "Nem jelzett",
          10: "Jön",
          11: "Megérkezett",
          20: "Nem jön"
        },
        grd_enum: {
          10: "Jól",
          20: "Meh",
          30: "Nem jól",
          40: "Nem volt jelen"
        },
        statusEnum: {
          "1": "Lezárva",
          "20": "Törölve",
          "10": "Megtartva",
          "6": "Adminoknak email kiküldve!",
          "5": "Emlékeztető email kiküldve a szülőknek!",
          "0": "",
          "" : ""
        },
        eventId: null,
        loading: true,
        remTime: {},
        attendanceState: {},
        balances: {},
        showCancel: false,
        cancelReason: ''
      };
    },
    computed: {
      filteredGroups() {
        return this.groups;
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isTeacher() {
        return this.$store.getters.isTeacher;
      },
      isCompleted() {
        return this.filteredGroups && this.filteredGroups.length > 0 && this.filteredGroups[0].status.String === '1';
      }
    },
    methods: {
      async getGroupEvents() {
        this.loading = true;
        try {
          const response = await axios.post(this.$BACKEND_HOST + '/api/groupeventwithid', {
            evnt_id: this.eventId,
          });
          if (response.data.message === "done") {
            this.groups = response.data.groups;
            this.groups_info = response.data.groups_info;
            if (this.groups !== null) {
              this.groups.forEach(grp => {
                grp.resp = this.resp_enum[grp.response.Int64];
                grp.evnt_grd = this.grd_enum[grp.attended.Int64];
                this.attendanceState[grp.child_id] = grp.attended.Int64 == 40 ? 2 : 1;
              });
            }
          }
          this.getRemainingTime(this.groups[0].set_date)
          this.fetchBalances();
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          this.loading = false;
        }
      },
      formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString();
      },
      getGroupNameById(groupId) {
        const group = this.groups_info.find(g => g.id === groupId);
        return group ? group.name : 'Ismeretlen csoport';
      },
    getRemainingTime(targetDate) {
      const now = new Date();
      const target = new Date(targetDate);
      const timeDifference = target - now;

      if (timeDifference <= 0) {
        this.remTime = { days: 0, hours: 0, minutes: 0, seconds: 0, passed: true };
        return;
      }

      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

      this.remTime = {
        days: days,
        hours: hours,
        minutes: minutes,
        seconds: seconds,
        passed: false
      }
    },
    async setNewResponse(eventId, childId, newResponse) {
      try {
          const response = await axios.post(this.$BACKEND_HOST + '/api/updatechildresponse', {
            evnt_id: eventId,
            child_id: childId,
            new_resp: newResponse
          });
          if (response.data.message === "done") {
           this.getGroupEvents();
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } 
    },

    // ----- New student-centric attendance (Jelenléti ív) -----
    defaultState(event) {
      return event.attended.Int64 == 40 ? 2 : 1;
    },
    async setAttendance(childId, state) {
      try {
        await axios.post(this.$BACKEND_HOST + '/api/attendance/state', {
          child_id: childId,
          event_log_id: this.eventId,
          state: state
        });
        this.attendanceState[childId] = state;
        if (state === 1 || state === 3) {
          this.fetchBalance(childId);
        }
      } catch (e) { console.error(e); }
    },
    async allPresent() {
      try {
        await axios.post(this.$BACKEND_HOST + `/api/attendance/all-present/${this.eventId}`);
        this.groups.forEach(g => { this.attendanceState[g.child_id] = 1; });
        this.fetchBalances();
      } catch (e) { console.error(e); }
    },
    async fetchBalances() {
      if (!this.groups) return;
      for (const g of this.groups) {
        await this.fetchBalance(g.child_id);
      }
    },
    async fetchBalance(childId) {
      try {
        const r = await axios.get(this.$BACKEND_HOST + `/api/students/${childId}/balance`);
        this.$set(this.balances, childId, r.data.in_debt === true);
      } catch (e) { console.error(e); }
    },
    async cancelClass() {
      try {
        await axios.post(this.$BACKEND_HOST + '/api/event/cancel', {
          event_log_id: this.eventId,
          reason: this.cancelReason
        });
        this.showCancel = false;
        this.cancelReason = '';
        this.getGroupEvents();
      } catch (e) { console.error(e); }
    },
    async completeLesson() {
      try {
        this.error = '';
        console.log('Completing lesson:', this.eventId);
        const response = await axios.post(this.$BACKEND_HOST + '/api/event/complete', {
          event_log_id: this.eventId
        });
        console.log('Complete lesson response:', response.data);
        this.getGroupEvents();
        window.dispatchEvent(new Event('billing:refresh'));
      } catch (e) {
        console.error('Complete lesson error:', e);
        this.error = 'Hiba a lecke lezárásakor: ' + (e.response?.data?.message || e.message);
      }
    },
    
    // Helper methods for styling
    getStatusClass(status) {
      const statusClasses = {
        "20": "cancelled",
        "10": "confirmed", 
        "6": "email-sent",
        "5": "reminder-sent",
        "0": "draft",
        "": "draft"
      };
      return statusClasses[status] || "draft";
    },
    
    getResponseClass(response) {
      const responseClasses = {
        0: "not-responded",
        10: "attending", 
        11: "arrived",
        20: "not-attending"
      };
      return responseClasses[response] || "not-responded";
    },
    
    getFeelingClass(feeling) {
      const feelingClasses = {
        10: "good",
        20: "okay",
        30: "bad", 
        40: "absent"
      };
      return feelingClasses[feeling] || "unknown";
    }
    },
    watch: {
      eventDate(newValue) {
        this.getGroupEvents();
      }
    },
    mounted() {
      this.eventId = parseInt(this.$route.params.id);
      this.getGroupEvents();
    }
  };
  </script>
  
<style scoped>
/* Main Wrapper */
.event-info-wrapper {
  min-height: 100vh;
  background: var(--color-background);
  padding: 2rem 0;
}

/* Header Section */
.content-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  margin-bottom: 2rem;
  border: 1px solid var(--color-border);
}

.content-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-heading);
  margin: 0 0 0.5rem 0;
}

.content-header p {
  color: var(--color-text-muted);
  margin: 0;
  font-size: 1rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}

/* Loading Container */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 0;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-radius: 50%;
  border-top-color: var(--button-primary);
  animation: spin 1s ease-in-out infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Event Details Card */
.event-details-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.event-details-card:hover {
  box-shadow: var(--shadow-md);
}

.card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-background-soft);
}

.card-header h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.card-body {
  padding: 2rem;
}

/* Info Grid */
.info-grid {
  display: grid;
  gap: 1.5rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0;
  border: none;
  background: none;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  font-weight: 600;
  color: var(--color-heading);
  font-size: 0.95rem;
}

.info-label svg {
  flex-shrink: 0;
  color: var(--button-primary);
}

.info-value {
  flex: 1;
  color: var(--color-text);
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Status Badge */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge.confirmed {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.status-badge.cancelled {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.status-badge.email-sent {
  background: rgba(59, 130, 246, 0.1);
  color: var(--info);
}

.status-badge.reminder-sent {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.status-badge.draft {
  background: rgba(107, 114, 128, 0.1);
  color: var(--color-text-muted);
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 2rem;
  background: var(--color-background-soft);
  border-bottom: 1px solid var(--color-border);
}

.section-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-heading);
  margin: 0;
}

.section-icon {
  width: 32px;
  height: 32px;
  background: var(--button-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Table Container */
.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.modern-table th {
  background: var(--color-background-soft);
  padding: 1rem 1.5rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-heading);
  border-bottom: 1px solid var(--color-border);
  font-size: 0.875rem;
}

.modern-table td {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  vertical-align: middle;
}

.modern-table tr:hover {
  background: var(--color-background-soft);
}

.modern-table tr:last-child td {
  border-bottom: none;
}

.actions-column {
  text-align: center;
  min-width: 120px;
}

/* Participant Info */
.participant-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.participant-avatar {
  width: 36px;
  height: 36px;
  background: var(--color-background-mute);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.participant-name {
  font-weight: 500;
  color: var(--color-text);
}

/* Response and Feeling Badges */
.response-badge,
.feeling-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.response-badge.not-responded {
  background: rgba(107, 114, 128, 0.1);
  color: var(--color-text-muted);
}

.response-badge.attending {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.response-badge.arrived {
  background: rgba(59, 130, 246, 0.1);
  color: var(--info);
}

.response-badge.not-attending {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.feeling-badge.good {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.feeling-badge.okay {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.feeling-badge.bad {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.feeling-badge.absent {
  background: rgba(107, 114, 128, 0.1);
  color: var(--color-text-muted);
}

.feeling-badge.unknown {
  background: rgba(107, 114, 128, 0.1);
  color: var(--color-text-muted);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--color-text-muted);
}

.empty-state svg {
  width: 64px;
  height: 64px;
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--color-heading);
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .event-info-wrapper {
    padding: 1rem 0;
  }
  
  .content-header {
    padding: 1.25rem 1.5rem;
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .content-header h1 {
    font-size: 1.75rem;
  }
  
  .header-icon {
    width: 56px;
    height: 56px;
  }
  
  .card-header {
    padding: 1.25rem 1.5rem;
  }
  
  .card-body {
    padding: 1.5rem;
  }
  
  .info-grid {
    gap: 1.25rem;
  }
  
  .info-item {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .info-label {
    min-width: auto;
  }
  
  .section-header {
    padding: 1.25rem 1.5rem;
  }
  
  .section-header h2 {
    font-size: 1.125rem;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.375rem;
  }
  
  .empty-state {
    padding: 3rem 1rem;
  }
}

@media (max-width: 480px) {
  .content-header {
    padding: 1rem 1.25rem;
  }
  
  .content-header h1 {
    font-size: 1.5rem;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
  }
  
  .card-header,
  .card-body {
    padding: 1rem 1.25rem;
  }
  
  .section-header {
    padding: 1rem 1.25rem;
  }
  
  .info-grid {
    gap: 1rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.75rem 1rem;
  }
  
  .participant-info {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .action-buttons .modern-btn {
    width: 100%;
    justify-content: center;
  }
  
  .empty-state {
    padding: 2rem 0.75rem;
  }
}

/* New student-centric attendance */
.debt-row {
  background: rgba(220, 38, 38, 0.18) !important;
  animation: debt-flash 1.2s ease-in-out infinite;
}
@keyframes debt-flash {
  0%, 100% { background: rgba(220, 38, 38, 0.12) !important; }
  50% { background: rgba(220, 38, 38, 0.32) !important; }
}
.attendance-states { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.attendance-states .modern-btn.active {
  outline: 3px solid #fff;
  box-shadow: 0 0 0 2px currentColor;
}
.modern-btn--warning { background: #d97706; }
.modern-btn--warning:hover { background: #b45309; }
.section-actions { display: flex; gap: 0.6rem; margin-left: auto; flex-wrap: wrap; }

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .section-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
  }

  .section-actions .modern-btn {
    flex: 1 1 auto;
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
  }
}

/* Cancellation modal */
.mdl-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.mdl-content {
  background: var(--card-bg); border-radius: 16px; box-shadow: var(--shadow-lg);
  max-width: 540px; width: 90%; padding: 0; overflow: hidden;
}
.mdl-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--color-border);
  background: var(--color-background-soft);
}
.mdl-header h3 { margin: 0; }
.close-btn { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: var(--color-text-muted); }
.mdl-body { padding: 1.5rem; display: flex; flex-direction: column; gap: 0.8rem; }
.mdl-body textarea { width: 100%; min-height: 90px; border-radius: 8px; padding: 0.6rem; border: 1px solid var(--color-border); background: var(--card-bg); color: var(--color-text); }
.mdl-actions { display: flex; gap: 0.6rem; justify-content: flex-end; }
</style>
  